import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type { ApiKeyInfo } from '../api/client';
import { Btn, Card, ErrorLine, Loading, Row, Section, XButton } from './ChatAssistant/compact/kit';
import { C } from './ChatAssistant/compact/tokens';
import { font as f } from './ChatAssistant/compact/style';

/** Settings → Account → API key: the person's own key for the external API. */
export function ApiKeyManagement() {
  const { t } = useTranslation('account');
  const [keyInfo, setKeyInfo] = useState<ApiKeyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'regenerate' | 'revoke' | null>(null);

  useEffect(() => {
    loadKeyInfo();
  }, []);

  const loadKeyInfo = async () => {
    try {
      setLoading(true);
      const info = await api.getMyApiKeyInfo();
      setKeyInfo(info);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load API key info:', err);
      setError(err.message || t('apiKey.loadInfoFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const result = await api.generateMyApiKey();
      setGeneratedKey(result.api_key);
      setKeyInfo({ has_key: true, key_prefix: result.key_prefix });
      setConfirmAction(null);
    } catch (err: any) {
      console.error('Failed to generate API key:', err);
      setError(err.message || t('apiKey.generateFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async () => {
    try {
      setActionLoading(true);
      setError(null);
      await api.revokeMyApiKey();
      setKeyInfo({ has_key: false });
      setGeneratedKey(null);
      setConfirmAction(null);
    } catch (err: any) {
      console.error('Failed to revoke API key:', err);
      setError(err.message || t('apiKey.revokeFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedKey) return;
    try {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generatedKey;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return t('apiKey.never');
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const mono = { font: f(500, 12.5), fontFamily: 'var(--font-mono)', color: C.text } as const;

  return (
    <Section title={t('apiKey.title')} hint={t('apiKey.description')}>
      {error && (
        <ErrorLine>
          <span style={{ flex: 1 }}>{error}</span>
          <XButton onClick={() => setError(null)} />
        </ErrorLine>
      )}

      {loading ? (
        <Card><div style={{ padding: '0 14px' }}><Loading /></div></Card>
      ) : generatedKey ? (
        /* One-time key reveal after generation */
        <Card>
          <Row stack label={t('apiKey.key')} help={t('apiKey.storeSecurely')} helpColor={C.purplePale}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <code style={{ ...mono, flex: '1 1 260px', minWidth: 0, padding: '6px 8px', borderRadius: 6, background: C.input, border: `1px solid ${C.border}`, wordBreak: 'break-all' }}>{generatedKey}</code>
              <Btn kind={copied ? 'tint' : 'ghost'} icon={copied ? 'check' : 'copy'} onClick={() => void handleCopy()}>
                {copied ? t('apiKey.copied') : t('apiKey.copy')}
              </Btn>
            </div>
          </Row>
          <div style={{ padding: '0 14px 12px' }}>
            <Btn kind="primary" onClick={() => setGeneratedKey(null)}>{t('apiKey.savedMyKey')}</Btn>
          </div>
        </Card>
      ) : keyInfo && !keyInfo.has_key ? (
        <Card>
          <Row label={t('apiKey.noKeyYet')} help={t('apiKey.noKeyHint')}>
            <Btn kind="primary" icon="key" onClick={() => void handleGenerate()} disabled={actionLoading}>
              {actionLoading ? t('apiKey.generating') : t('apiKey.generateApiKey')}
            </Btn>
          </Row>
        </Card>
      ) : keyInfo ? (
        <Card>
          <Row label={t('apiKey.key')}>
            <code style={mono}>{keyInfo.key_prefix}••••••••</code>
          </Row>
          <Row label={t('apiKey.created')}><span style={{ font: f(500, 12.5), color: C.sub }}>{formatDate(keyInfo.created_at)}</span></Row>
          <Row label={t('apiKey.lastUsed')}><span style={{ font: f(500, 12.5), color: C.sub }}>{formatDate(keyInfo.last_used_at)}</span></Row>
          {confirmAction ? (
            <Row
              stack
              label={confirmAction === 'regenerate' ? t('apiKey.regenerateTitle') : t('apiKey.revokeTitle')}
              help={confirmAction === 'regenerate' ? t('apiKey.regenerateMessage') : t('apiKey.revokeMessage')}
            >
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn kind="primary" onClick={() => void (confirmAction === 'regenerate' ? handleGenerate() : handleRevoke())} disabled={actionLoading}>
                  {actionLoading ? t('apiKey.processing') : confirmAction === 'regenerate' ? t('apiKey.regenerate') : t('apiKey.revoke')}
                </Btn>
                <Btn kind="muted" onClick={() => setConfirmAction(null)} disabled={actionLoading}>{t('apiKey.cancel')}</Btn>
              </div>
            </Row>
          ) : (
            <div style={{ display: 'flex', gap: 8, padding: '6px 14px 12px' }}>
              <Btn icon="refresh-cw" onClick={() => setConfirmAction('regenerate')} disabled={actionLoading}>{t('apiKey.regenerate')}</Btn>
              <Btn kind="muted" icon="slash" onClick={() => setConfirmAction('revoke')} disabled={actionLoading}>{t('apiKey.revoke')}</Btn>
            </div>
          )}
        </Card>
      ) : null}
    </Section>
  );
}
